import { cn } from '@shadcn/lib/utils';
import { Calendar } from '@shadcn/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@shadcn/ui/popover';
import { format } from 'date-fns';
import { CircleX } from 'lucide-react';
import React, { useCallback, useId, useState } from 'react';

import { Button } from '../button/button';

function ClearDateButton({ onClick }: { readonly onClick: (event: React.MouseEvent<HTMLButtonElement>) => void }) {
  return (
    <Button appearance="secondary" aria-label="Clear date" className="rounded-l-none" onClick={onClick} size="sm">
      <CircleX />
    </Button>
  );
}

type ClinicianDatePickerProps = {
  readonly date: Date | undefined;
  readonly setDate: (date: Date | undefined) => void;
  readonly id?: string;
  readonly ariaLabelledby?: string;
  readonly className?: string;
  readonly buttonText?: string;
  readonly captionLayout?: React.ComponentProps<typeof Calendar>['captionLayout'];
  readonly fromYear?: number;
  readonly toYear?: number;
  readonly minDate?: Date;
  readonly maxDate?: Date;
};

function useClinicianDatePickerState({
  setDate,
  minDate,
  maxDate,
}: {
  readonly setDate: (date: Date | undefined) => void;
  readonly minDate?: Date;
  readonly maxDate?: Date;
}) {
  const generatedId = useId();
  const [open, setOpen] = useState(false);

  const clearHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDate(undefined);
    },
    [setDate]
  );

  const isDateDisabled = useCallback(
    (calendarDate: Date) => {
      const isAfterToday = calendarDate > new Date();
      const isBeforeMinDate = minDate ? calendarDate < minDate : false;
      const isAfterMaxDate = maxDate ? calendarDate > maxDate : false;

      return isAfterToday || isBeforeMinDate || isAfterMaxDate;
    },
    [minDate, maxDate]
  );

  const selectHandler = useCallback(
    (d: Date | undefined) => {
      setDate(d);
      if (d) {
        setOpen(false);
      }
    },
    [setDate]
  );

  return {
    clearHandler,
    generatedId,
    isDateDisabled,
    open,
    selectHandler,
    setOpen,
  };
}

function ClinicianDatePicker({
  date,
  setDate,
  id: incomingId,
  ariaLabelledby,
  className,
  buttonText,
  captionLayout,
  fromYear,
  toYear,
  minDate,
  maxDate,
}: ClinicianDatePickerProps) {
  const { generatedId, open, setOpen, clearHandler, isDateDisabled, selectHandler } = useClinicianDatePickerState({
    setDate,
    minDate,
    maxDate,
  });

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div className={cn('flex', className)}>
          <Button
            appearance="secondary"
            aria-labelledby={ariaLabelledby}
            className="rounded-r-none border-r-0"
            id={incomingId ?? generatedId}
            size="sm"
          >
            <span>{buttonText || (date ? format(date, 'PPP') : 'Select a date')}</span>
          </Button>
          <ClearDateButton onClick={clearHandler} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          captionLayout={captionLayout}
          defaultMonth={date}
          disabled={isDateDisabled}
          fromYear={fromYear}
          initialFocus
          mode="single"
          onSelect={selectHandler}
          selected={date}
          toYear={toYear}
        />
      </PopoverContent>
    </Popover>
  );
}

export { ClinicianDatePicker };
export type { ClinicianDatePickerProps };
